import React, { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  LayoutDashboard, 
  Package, 
  Inbox, 
  LogOut, 
  Plus, 
  Edit2, 
  Trash2, 
  Upload,
  X,
  Check,
  ChevronDown,
  FolderOpen
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth, resolveImageUrl } from "../App";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

// Admin Sidebar
const AdminSidebar = () => {
  const location = useLocation();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const links = [
    { path: "/admin", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/admin/products", icon: Package, label: "Products" },
    { path: "/admin/collections", icon: FolderOpen, label: "Collections" },
    { path: "/admin/requests", icon: Inbox, label: "Requests" },
  ];

  return (
    <aside className="w-64 bg-[#0A0A0A] border-r border-white/5 min-h-screen fixed left-0 top-0">
      <div className="p-6 border-b border-white/5">
        <Link to="/" className="font-['Playfair_Display'] text-xl text-white">
          INTROZZI
        </Link>
        <p className="text-xs text-gray-500 mt-1">Admin Panel</p>
      </div>

      <nav className="p-4 space-y-1">
        {links.map((link) => {
          const isActive = location.pathname === link.path;
          return (
            <Link
              key={link.path}
              to={link.path}
              className={`flex items-center gap-3 px-4 py-3 rounded text-sm transition-colors ${
                isActive 
                  ? "bg-white/10 text-white" 
                  : "text-gray-400 hover:text-white hover:bg-white/5"
              }`}
              data-testid={`admin-nav-${link.label.toLowerCase()}`}
            >
              <link.icon size={18} strokeWidth={1.5} />
              {link.label}
            </Link>
          );
        })}
      </nav>

      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/5">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-3 w-full text-gray-400 hover:text-white hover:bg-white/5 rounded text-sm transition-colors"
          data-testid="admin-logout-btn"
        >
          <LogOut size={18} strokeWidth={1.5} />
          Sign Out
        </button>
      </div>
    </aside>
  );
};

// Dashboard Overview
const DashboardOverview = () => {
  const [stats, setStats] = useState({ products: 0, requests: 0, newRequests: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [productsRes, requestsRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/admin/requests`, { withCredentials: true })
        ]);
        const newRequests = requestsRes.data.filter(r => r.status === "new").length;
        setStats({
          products: productsRes.data.length,
          requests: requestsRes.data.length,
          newRequests
        });
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };
    fetchStats();
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-2xl font-['Playfair_Display'] text-white mb-8">Dashboard</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-[#121212] border border-white/5 p-6">
          <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Total Products</p>
          <p className="text-3xl text-white font-light">{stats.products}</p>
        </div>
        <div className="bg-[#121212] border border-white/5 p-6">
          <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Total Requests</p>
          <p className="text-3xl text-white font-light">{stats.requests}</p>
        </div>
        <div className="bg-[#121212] border border-white/5 p-6">
          <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">New Requests</p>
          <p className="text-3xl text-[#C5A059] font-light">{stats.newRequests}</p>
        </div>
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link 
          to="/admin/products"
          className="bg-[#121212] border border-white/5 p-6 hover:border-white/20 transition-colors group"
        >
          <Package size={24} className="text-gray-500 group-hover:text-white transition-colors mb-4" strokeWidth={1.5} />
          <h3 className="text-white mb-2">Manage Products</h3>
          <p className="text-gray-500 text-sm">Add, edit, or remove products from your catalog.</p>
        </Link>
        <Link 
          to="/admin/requests"
          className="bg-[#121212] border border-white/5 p-6 hover:border-white/20 transition-colors group"
        >
          <Inbox size={24} className="text-gray-500 group-hover:text-white transition-colors mb-4" strokeWidth={1.5} />
          <h3 className="text-white mb-2">View Requests</h3>
          <p className="text-gray-500 text-sm">Review and manage customer inquiries.</p>
        </Link>
      </div>
    </div>
  );
};

// Products Management
const ProductsManagement = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    sku: "",
    collection: "",
    material: "",
    finish: "",
    dimensions: "",
    is_featured: false,
    seo_title: "",
    seo_description: ""
  });
  const [uploading, setUploading] = useState(false);
  const [collections, setCollections] = useState([]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  const fetchCollections = async () => {
    try {
      const response = await axios.get(`${API}/collections/names`);
      setCollections(response.data);
    } catch (error) {
      console.error("Failed to fetch collections");
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCollections();
  }, []);

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name,
      slug: editingProduct ? prev.slug : generateSlug(name)
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, formData, { withCredentials: true });
        toast.success("Product updated successfully");
      } else {
        await axios.post(`${API}/admin/products`, formData, { withCredentials: true });
        toast.success("Product created successfully");
      }
      setShowModal(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Failed to save product");
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || "",
      slug: product.slug || "",
      description: product.description || "",
      sku: product.sku || "",
      collection: product.collection || "",
      material: product.material || "",
      finish: product.finish || "",
      dimensions: product.dimensions || "",
      is_featured: product.is_featured || false,
      seo_title: product.seo_title || "",
      seo_description: product.seo_description || ""
    });
    setShowModal(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    
    try {
      await axios.delete(`${API}/admin/products/${productId}`, { withCredentials: true });
      toast.success("Product deleted successfully");
      fetchProducts();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file || !editingProduct) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const uploadRes = await axios.post(`${API}/admin/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Use the public URL returned by the upload endpoint
      const imageUrl = uploadRes.data.url;

      let updateData = {};
      if (type === 'cover') {
        updateData = { cover_image: imageUrl };
      } else if (type === 'gallery') {
        const currentGallery = editingProduct.gallery_images || [];
        updateData = { gallery_images: [...currentGallery, imageUrl] };
      } else if (type === 'hero') {
        updateData = { hero_image: imageUrl };
      }

      await axios.put(`${API}/admin/products/${editingProduct.id}`, updateData, { withCredentials: true });
      toast.success("Image uploaded successfully");
      
      // Refresh product data
      const updatedProduct = await axios.get(`${API}/products/${editingProduct.slug}`);
      setEditingProduct(updatedProduct.data);
      fetchProducts();
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (type, index) => {
    if (!editingProduct) return;

    const confirmMessage = type === 'gallery' 
      ? 'Are you sure you want to delete this gallery image?' 
      : `Are you sure you want to delete the ${type} image?`;
    
    if (!window.confirm(confirmMessage)) return;

    setUploading(true);

    try {
      let updateData = {};
      
      if (type === 'cover') {
        updateData = { cover_image: null };
      } else if (type === 'gallery' && index !== null) {
        const currentGallery = [...(editingProduct.gallery_images || [])];
        currentGallery.splice(index, 1);
        updateData = { gallery_images: currentGallery };
      } else if (type === 'hero') {
        updateData = { hero_image: null };
      }

      await axios.put(`${API}/admin/products/${editingProduct.id}`, updateData, { withCredentials: true });
      toast.success("Image deleted successfully");
      
      // Refresh product data
      const updatedProduct = await axios.get(`${API}/products/${editingProduct.slug}`);
      setEditingProduct(updatedProduct.data);
      fetchProducts();
    } catch (error) {
      toast.error("Failed to delete image");
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      slug: "",
      description: "",
      sku: "",
      collection: "",
      material: "",
      finish: "",
      dimensions: "",
      is_featured: false,
      seo_title: "",
      seo_description: ""
    });
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-['Playfair_Display'] text-white">Products</h1>
        <button
          onClick={() => {
            setEditingProduct(null);
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center gap-2 bg-white text-black px-4 py-2 text-sm hover:bg-gray-200 transition-colors"
          data-testid="add-product-btn"
        >
          <Plus size={16} strokeWidth={1.5} />
          Add Product
        </button>
      </div>

      {loading ? (
        <div className="text-gray-500">Loading...</div>
      ) : products.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No products yet. Click "Add Product" to create one.
        </div>
      ) : (
        <div className="space-y-4">
          {products.map((product) => (
            <div 
              key={product.id}
              className="bg-[#121212] border border-white/5 p-4 flex items-center gap-4"
              data-testid={`product-item-${product.slug}`}
            >
              <div className="w-16 h-16 bg-[#0A0A0A] flex-shrink-0">
                {product.cover_image ? (
                  <img src={resolveImageUrl(product.cover_image)} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs">
                    No img
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-white truncate">{product.name}</h3>
                  {product.is_featured && (
                    <span className="text-xs bg-[#C5A059]/20 text-[#C5A059] px-2 py-0.5">Featured</span>
                  )}
                </div>
                <p className="text-gray-500 text-sm">{product.sku}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleEdit(product)}
                  className="p-2 text-gray-400 hover:text-white transition-colors"
                  data-testid={`edit-product-${product.slug}`}
                >
                  <Edit2 size={16} strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => handleDelete(product.id)}
                  className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                  data-testid={`delete-product-${product.slug}`}
                >
                  <Trash2 size={16} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Product Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 overflow-y-auto py-8">
          <motion.div
            className="bg-[#0A0A0A] border border-white/10 w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10 flex-shrink-0">
              <h2 className="text-xl text-white font-['Playfair_Display']">
                {editingProduct ? "Edit Product" : "Add Product"}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1" data-testid="product-form">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleNameChange}
                    required
                    className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white text-sm focus:outline-none focus:border-white/30"
                    data-testid="product-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Slug *</label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white text-sm focus:outline-none focus:border-white/30"
                    data-testid="product-slug-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">SKU *</label>
                <input
                  type="text"
                  name="sku"
                  value={formData.sku}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white text-sm focus:outline-none focus:border-white/30"
                  data-testid="product-sku-input"
                />
              </div>

              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white text-sm focus:outline-none focus:border-white/30 resize-none"
                  data-testid="product-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Collection *</label>
                  <div className="relative">
                    <select
                      name="collection"
                      value={formData.collection}
                      onChange={handleChange}
                      required
                      className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white text-sm focus:outline-none focus:border-white/30 appearance-none cursor-pointer"
                      data-testid="product-collection-select"
                    >
                      <option value="">Select a collection</option>
                      {collections.map((col) => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                      <option value="__new__">+ Add new collection</option>
                    </select>
                    <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                  </div>
                  {formData.collection === "__new__" && (
                    <input
                      type="text"
                      placeholder="Enter new collection name"
                      onChange={(e) => setFormData(prev => ({ ...prev, collection: e.target.value }))}
                      className="w-full mt-2 bg-[#121212] border border-white/10 px-4 py-3 text-white text-sm focus:outline-none focus:border-white/30"
                      autoFocus
                    />
                  )}
                </div>
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Material</label>
                  <input
                    type="text"
                    name="material"
                    value={formData.material}
                    onChange={handleChange}
                    className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white text-sm focus:outline-none focus:border-white/30"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Finish</label>
                  <input
                    type="text"
                    name="finish"
                    value={formData.finish}
                    onChange={handleChange}
                    className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white text-sm focus:outline-none focus:border-white/30"
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Dimensions</label>
                  <input
                    type="text"
                    name="dimensions"
                    value={formData.dimensions}
                    onChange={handleChange}
                    className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white text-sm focus:outline-none focus:border-white/30"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_featured"
                  name="is_featured"
                  checked={formData.is_featured}
                  onChange={handleChange}
                  className="w-4 h-4"
                  data-testid="product-featured-checkbox"
                />
                <label htmlFor="is_featured" className="text-white text-sm">Feature on Homepage</label>
              </div>

              <div className="border-t border-white/10 pt-6">
                <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-4">SEO Settings</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">Meta Title</label>
                    <input
                      type="text"
                      name="seo_title"
                      value={formData.seo_title}
                      onChange={handleChange}
                      className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white text-sm focus:outline-none focus:border-white/30"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">Meta Description</label>
                    <textarea
                      name="seo_description"
                      value={formData.seo_description}
                      onChange={handleChange}
                      rows={2}
                      className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white text-sm focus:outline-none focus:border-white/30 resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Image Upload Section - Only for editing */}
              {editingProduct && (
                <div className="border-t border-white/10 pt-6">
                  <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-4">Images</p>
                  
                  <div className="space-y-6">
                    {/* Cover Image */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-2">Cover Image</label>
                      <div className="flex items-center gap-4">
                        {editingProduct.cover_image ? (
                          <div className="relative group">
                            <img src={resolveImageUrl(editingProduct.cover_image)} alt="Cover" className="w-20 h-20 object-cover" />
                            <button
                              type="button"
                              onClick={() => handleDeleteImage('cover', null)}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Delete cover image"
                            >
                              <X size={14} className="text-white" />
                            </button>
                          </div>
                        ) : (
                          <div className="w-20 h-20 bg-[#121212] flex items-center justify-center text-gray-600 text-xs">
                            No cover
                          </div>
                        )}
                        <label className="flex items-center gap-2 px-4 py-2 bg-[#121212] border border-white/10 text-gray-400 hover:text-white cursor-pointer transition-colors rounded">
                          <Upload size={16} strokeWidth={1.5} />
                          <span className="text-sm">{editingProduct.cover_image ? 'Change Cover' : 'Upload Cover'}</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, 'cover')}
                            className="hidden"
                            disabled={uploading}
                          />
                        </label>
                      </div>
                    </div>

                    {/* Gallery Images */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-2">Gallery Images</label>
                      {editingProduct.gallery_images && editingProduct.gallery_images.length > 0 ? (
                        <div className="flex flex-wrap gap-3 mb-3">
                          {editingProduct.gallery_images.map((img, idx) => (
                            <div key={idx} className="relative group">
                              <img src={resolveImageUrl(img)} alt={`Gallery ${idx + 1}`} className="w-20 h-20 object-cover" />
                              <button
                                type="button"
                                onClick={() => handleDeleteImage('gallery', idx)}
                                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Delete this image"
                              >
                                <X size={14} className="text-white" />
                              </button>
                              <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[10px] text-center py-0.5">
                                {idx + 1}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-600 text-sm mb-3">No gallery images</p>
                      )}
                      <label className="flex items-center gap-2 px-4 py-2 bg-[#121212] border border-white/10 text-gray-400 hover:text-white cursor-pointer transition-colors w-fit rounded">
                        <Upload size={16} strokeWidth={1.5} />
                        <span className="text-sm">Add to Gallery</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, 'gallery')}
                          className="hidden"
                          disabled={uploading}
                        />
                      </label>
                    </div>

                    {/* Hero Image */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-2">Hero Image (Optional - for product page header)</label>
                      <div className="flex items-center gap-4">
                        {editingProduct.hero_image ? (
                          <div className="relative group">
                            <img src={resolveImageUrl(editingProduct.hero_image)} alt="Hero" className="w-20 h-20 object-cover" />
                            <button
                              type="button"
                              onClick={() => handleDeleteImage('hero', null)}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Delete hero image"
                            >
                              <X size={14} className="text-white" />
                            </button>
                          </div>
                        ) : (
                          <div className="w-20 h-20 bg-[#121212] flex items-center justify-center text-gray-600 text-xs">
                            No hero
                          </div>
                        )}
                        <label className="flex items-center gap-2 px-4 py-2 bg-[#121212] border border-white/10 text-gray-400 hover:text-white cursor-pointer transition-colors rounded">
                          <Upload size={16} strokeWidth={1.5} />
                          <span className="text-sm">{editingProduct.hero_image ? 'Change Hero' : 'Upload Hero'}</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, 'hero')}
                            className="hidden"
                            disabled={uploading}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 border border-white/20 text-white px-4 py-3 text-sm hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-white text-black px-4 py-3 text-sm hover:bg-gray-200 transition-colors"
                  data-testid="save-product-btn"
                >
                  {editingProduct ? "Save Changes" : "Create Product"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

// Collections Management
const CollectionsManagement = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [editingName, setEditingName] = useState(null);
  const [editNameValue, setEditNameValue] = useState("");

  const fetchCollections = async () => {
    try {
      const response = await axios.get(`${API}/collections`);
      setCollections(response.data);
    } catch (error) {
      toast.error("Failed to fetch collections");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCollections(); }, []);

  const handleCreate = async () => {
    if (!newCollectionName.trim()) return;
    try {
      await axios.post(`${API}/admin/collections`, { name: newCollectionName.trim() }, { withCredentials: true });
      toast.success("Collection created");
      setNewCollectionName("");
      setShowAddModal(false);
      fetchCollections();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create collection");
    }
  };

  const handleRename = async (oldName) => {
    if (!editNameValue.trim() || editNameValue.trim() === oldName) {
      setEditingName(null);
      return;
    }
    try {
      await axios.put(`${API}/admin/collections/${encodeURIComponent(oldName)}/rename`,
        { new_name: editNameValue.trim() },
        { withCredentials: true }
      );
      toast.success("Collection renamed");
      setEditingName(null);
      fetchCollections();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to rename");
    }
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`Delete "${name}"? Products in this collection will be unassigned.`)) return;
    try {
      await axios.delete(`${API}/admin/collections/${encodeURIComponent(name)}`, { withCredentials: true });
      toast.success("Collection deleted");
      fetchCollections();
    } catch (error) {
      toast.error("Failed to delete collection");
    }
  };

  const handleImageUpload = async (e, collectionName) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(collectionName);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    try {
      const uploadRes = await axios.post(`${API}/admin/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await axios.put(`${API}/admin/collections/${encodeURIComponent(collectionName)}`,
        { cover_image: uploadRes.data.url },
        { withCredentials: true }
      );
      toast.success("Cover image updated");
      fetchCollections();
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(null);
    }
  };

  const handleDeleteImage = async (collectionName) => {
    if (!window.confirm(`Remove cover image for "${collectionName}"?`)) return;
    setUploading(collectionName);
    try {
      await axios.put(`${API}/admin/collections/${encodeURIComponent(collectionName)}`,
        { cover_image: null },
        { withCredentials: true }
      );
      toast.success("Cover image removed");
      fetchCollections();
    } catch (error) {
      toast.error("Failed to remove image");
    } finally {
      setUploading(null);
    }
  };

  return (
    <div data-testid="admin-collections">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-['Playfair_Display'] text-white">Collections</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-white text-black px-4 py-2 text-sm hover:bg-gray-200 transition-colors"
          data-testid="add-collection-btn"
        >
          <Plus size={16} strokeWidth={1.5} />
          Add Collection
        </button>
      </div>

      {loading ? (
        <div className="text-gray-500">Loading...</div>
      ) : collections.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No collections yet. Click "Add Collection" to create one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {collections.map((col) => (
            <div
              key={col.name}
              className="bg-[#121212] border border-white/5 overflow-hidden"
              data-testid={`collection-card-${col.name}`}
            >
              {/* Cover Image */}
              <div className="relative aspect-[16/9] bg-[#0A0A0A]">
                {col.cover_image ? (
                  <>
                    <img src={resolveImageUrl(col.cover_image)} alt={col.name} className="w-full h-full object-cover" />
                    <button
                      onClick={() => handleDeleteImage(col.name)}
                      className="absolute top-2 right-2 w-7 h-7 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-300 hover:text-red-400 transition-colors"
                      title="Remove cover image"
                    >
                      <X size={14} />
                    </button>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-600 text-sm">
                    No cover image
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  {editingName === col.name ? (
                    <input
                      value={editNameValue}
                      onChange={(e) => setEditNameValue(e.target.value)}
                      onBlur={() => handleRename(col.name)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleRename(col.name); if (e.key === 'Escape') setEditingName(null); }}
                      className="bg-[#0A0A0A] border border-white/20 px-2 py-1 text-white text-sm focus:outline-none focus:border-white/40 flex-1 mr-2"
                      autoFocus
                      data-testid={`rename-collection-input-${col.name}`}
                    />
                  ) : (
                    <h3 className="text-white font-medium truncate flex-1">{col.name}</h3>
                  )}
                  <span className="text-xs text-gray-500 flex-shrink-0 ml-2">{col.product_count} {col.product_count === 1 ? 'product' : 'products'}</span>
                </div>

                <div className="flex gap-2">
                  <label className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#0A0A0A] border border-white/10 text-gray-400 hover:text-white cursor-pointer transition-colors rounded text-xs ${uploading === col.name ? 'opacity-50 pointer-events-none' : ''}`}>
                    <Upload size={12} strokeWidth={1.5} />
                    {uploading === col.name ? '...' : col.cover_image ? 'Change' : 'Cover'}
                    <input
                      type="file" accept="image/*"
                      onChange={(e) => handleImageUpload(e, col.name)}
                      className="hidden" disabled={uploading === col.name}
                    />
                  </label>
                  <button
                    onClick={() => { setEditingName(col.name); setEditNameValue(col.name); }}
                    className="px-3 py-2 bg-[#0A0A0A] border border-white/10 text-gray-400 hover:text-white transition-colors rounded"
                    title="Rename"
                    data-testid={`rename-collection-btn-${col.name}`}
                  >
                    <Edit2 size={12} strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={() => handleDelete(col.name)}
                    className="px-3 py-2 bg-[#0A0A0A] border border-white/10 text-gray-400 hover:text-red-400 transition-colors rounded"
                    title="Delete"
                    data-testid={`delete-collection-btn-${col.name}`}
                  >
                    <Trash2 size={12} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Collection Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <motion.div
            className="bg-[#0A0A0A] border border-white/10 w-full max-w-md mx-4 p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg text-white font-['Playfair_Display']">New Collection</h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-white">
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>
            <div className="mb-6">
              <label className="block text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Name *</label>
              <input
                type="text"
                value={newCollectionName}
                onChange={(e) => setNewCollectionName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
                placeholder="e.g. Aureo Collection"
                className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white text-sm focus:outline-none focus:border-white/30"
                autoFocus
                data-testid="new-collection-name-input"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 border border-white/20 text-white px-4 py-3 text-sm hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                className="flex-1 bg-white text-black px-4 py-3 text-sm hover:bg-gray-200 transition-colors"
                data-testid="create-collection-btn"
              >
                Create
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};


// Requests Management
const RequestsManagement = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/requests`, { withCredentials: true });
      setRequests(response.data);
    } catch (error) {
      toast.error("Failed to fetch requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const updateStatus = async (requestId, status) => {
    try {
      await axios.put(`${API}/admin/requests/${requestId}`, { status }, { withCredentials: true });
      toast.success("Status updated");
      fetchRequests();
      if (selectedRequest?.id === requestId) {
        setSelectedRequest(prev => ({ ...prev, status }));
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-500/20 text-blue-400';
      case 'contacted': return 'bg-yellow-500/20 text-yellow-400';
      case 'closed': return 'bg-green-500/20 text-green-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div data-testid="admin-requests">
      <h1 className="text-2xl font-['Playfair_Display'] text-white mb-8">Requests</h1>

      {loading ? (
        <div className="text-gray-500">Loading...</div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No requests yet.
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Requests List */}
          <div className="space-y-3">
            {requests.map((request) => (
              <div
                key={request.id}
                onClick={() => setSelectedRequest(request)}
                className={`bg-[#121212] border p-4 cursor-pointer transition-colors ${
                  selectedRequest?.id === request.id 
                    ? 'border-white/30' 
                    : 'border-white/5 hover:border-white/20'
                }`}
                data-testid={`request-item-${request.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-white">{request.first_name} {request.last_name}</h3>
                  <span className={`text-xs px-2 py-1 ${getStatusColor(request.status)}`}>
                    {request.status}
                  </span>
                </div>
                <p className="text-gray-500 text-sm mb-1">{request.product_name}</p>
                <p className="text-gray-600 text-xs">{formatDate(request.created_at)}</p>
              </div>
            ))}
          </div>

          {/* Request Detail */}
          {selectedRequest && (
            <motion.div
              className="bg-[#121212] border border-white/5 p-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl text-white">Request Details</h2>
                <button 
                  onClick={() => setSelectedRequest(null)}
                  className="text-gray-400 hover:text-white lg:hidden"
                >
                  <X size={20} strokeWidth={1.5} />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Product</p>
                  <p className="text-white">{selectedRequest.product_name}</p>
                  <p className="text-gray-500 text-sm">SKU: {selectedRequest.product_sku}</p>
                </div>

                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Customer</p>
                  <p className="text-white">{selectedRequest.first_name} {selectedRequest.last_name}</p>
                  <p className="text-gray-400 text-sm">{selectedRequest.email}</p>
                  <p className="text-gray-400 text-sm">{selectedRequest.phone}</p>
                  <p className="text-gray-400 text-sm">
                    {selectedRequest.country}
                    {selectedRequest.other_country && ` (${selectedRequest.other_country})`}
                  </p>
                </div>

                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Message</p>
                  <p className="text-gray-300 text-sm whitespace-pre-wrap">{selectedRequest.message}</p>
                </div>

                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Submitted</p>
                  <p className="text-gray-400 text-sm">{formatDate(selectedRequest.created_at)}</p>
                </div>

                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-3">Status</p>
                  <Select
                    value={selectedRequest.status}
                    onValueChange={(value) => updateStatus(selectedRequest.id, value)}
                  >
                    <SelectTrigger className="w-full bg-[#0A0A0A] border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="new" className="text-white">New</SelectItem>
                      <SelectItem value="contacted" className="text-white">Contacted</SelectItem>
                      <SelectItem value="closed" className="text-white">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      )}
    </div>
  );
};

// Main Admin Dashboard
const AdminDashboard = () => {
  return (
    <div className="min-h-screen bg-black" data-testid="admin-layout">
      <AdminSidebar />
      <main className="ml-64 p-8">
        <Routes>
          <Route path="/" element={<DashboardOverview />} />
          <Route path="/products" element={<ProductsManagement />} />
          <Route path="/collections" element={<CollectionsManagement />} />
          <Route path="/requests" element={<RequestsManagement />} />
        </Routes>
      </main>
    </div>
  );
};

export default AdminDashboard;
